/** Settings, Notifications, Email: the SMTP server, a test send, and email addresses as channels. */
import { CircleCheck, CircleX, Mail, Plus, Send, Trash2 } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { NotifyMember, SmtpSecurity, SmtpView } from '@orgus/protocol';
import { Button, ConfirmDialog, EmptyState, ErrorState, Input, RelativeTime, Segmented, Select, toast, toUiError } from '@orgus/ui';
import { addEmailChannel, removeSmtp, saveSmtp, testSmtp } from './api.ts';

const SECURITY_HINT: Record<SmtpSecurity, string> = {
  starttls: 'Usually port 587. The connection is upgraded to TLS before the password is sent.',
  tls: 'Usually port 465. TLS from the first byte.',
  none: 'Only for a relay on the hub machine itself. A password is never sent unencrypted to another host.',
};

/** The last send, as a status with its icon. */
function LastSend({ last }: { last: NonNullable<SmtpView['last']> }) {
  return (
    <span className={last.ok ? 'flex items-center gap-1 text-caption text-fg-subtle' : 'flex items-center gap-1 text-caption text-failed'}>
      {last.ok ? <CircleCheck className="size-3.5 text-passed" aria-hidden /> : <CircleX className="size-3.5" aria-hidden />}
      {last.ok ? 'Last email delivered' : `Last email failed: ${last.message ?? 'no reason given'}`} <RelativeTime ts={last.at} />
    </span>
  );
}

/**
 * A mail provider sends for this Orgus (Orgus Cloud: Cloudflare Email Sending): no server to fill in.
 * Set up: who sends and a test send. Not set up: what whoever runs this Orgus does.
 */
export function ProviderEmail({ smtp, onSent }: { smtp: SmtpView; onSent: () => void }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const provider = smtp.provider!;
  if (!smtp.configured) {
    return <EmptyState icon={<Mail />} title="Email is not set up on this Orgus" description={provider.setup ?? `${provider.name} sends email once it is set up.`} />;
  }
  const test = async () => {
    setBusy(true);
    setError(null);
    try {
      await testSmtp(to.trim());
      toast.success('Test email sent', to.trim());
      onSent();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-caption text-fg-muted">
          <CircleCheck className="size-3.5 text-passed" aria-hidden />
          {provider.name} sends from {smtp.from}
        </span>
        {smtp.last && <LastSend last={smtp.last} />}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Input label="Send a test to" type="email" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-72" />
        <Button variant="secondary" icon={<Send />} loading={busy} disabled={!to.includes('@')} onClick={() => void test()}>
          Send test email
        </Button>
      </div>
      {error != null && <ErrorState variant="inline" error={toUiError(error)} />}
    </div>
  );
}

export function SmtpForm({ smtp, onSaved }: { smtp: SmtpView; onSaved: () => void }) {
  const [host, setHost] = useState(smtp.host);
  const [port, setPort] = useState(String(smtp.port || 587));
  const [security, setSecurity] = useState<SmtpSecurity>(smtp.security);
  const [user, setUser] = useState(smtp.user ?? '');
  const [password, setPassword] = useState('');
  const [from, setFrom] = useState(smtp.from);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [to, setTo] = useState('');
  const [removing, setRemoving] = useState(false);
  useEffect(() => {
    setHost(smtp.host);
    setPort(String(smtp.port || 587));
    setSecurity(smtp.security);
    setUser(smtp.user ?? '');
    setFrom(smtp.from);
  }, [smtp]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      await saveSmtp({ host: host.trim(), port: Number(port), security, user: user.trim() || null, ...(password ? { password } : {}), from: from.trim() });
      setPassword('');
      toast.success('Email server saved', 'Send a test to check it.');
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };
  const test = async () => {
    setBusy('test');
    setError(null);
    try {
      await testSmtp(to.trim());
      toast.success('Test email sent', to.trim());
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={save} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_96px]">
          <Input label="Mail server" required placeholder="smtp.example.com" value={host} onChange={(e) => setHost(e.target.value)} spellCheck={false} autoComplete="off" />
          <Input label="Port" required inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-caption font-medium text-fg-muted">Security</span>
          <Segmented<SmtpSecurity>
            label="Security"
            className="self-start"
            value={security}
            onChange={(v) => {
              setSecurity(v);
              if (v === 'tls' && port === '587') setPort('465');
              if (v === 'starttls' && port === '465') setPort('587');
            }}
            options={[
              { value: 'starttls', label: 'STARTTLS' },
              { value: 'tls', label: 'TLS' },
              { value: 'none', label: 'None' },
            ]}
          />
          <p className="text-caption text-fg-subtle">{SECURITY_HINT[security]}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="User name" placeholder="Leave empty for no sign-in" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" spellCheck={false} />
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            placeholder={smtp.hasPassword ? 'Saved. Type to replace it.' : ''}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="Kept on the hub, never shown again."
            disabled={!user.trim()}
          />
        </div>
        <Input label="From" required placeholder="Orgus <orgus@example.com>" value={from} onChange={(e) => setFrom(e.target.value)} hint="Use an address your mail server may send as." />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" loading={busy === 'save'}>
            {smtp.configured ? 'Save email server' : 'Set up email'}
          </Button>
          {smtp.configured && (
            <Button variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(true)}>
              Remove
            </Button>
          )}
          {smtp.last && <LastSend last={smtp.last} />}
        </div>
      </form>
      {smtp.configured && (
        <div className="flex flex-wrap items-end gap-2 border-t border-border-subtle pt-4">
          <Input label="Send a test to" type="email" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-72" />
          <Button variant="secondary" icon={<Send />} loading={busy === 'test'} disabled={!to.includes('@')} onClick={() => void test()}>
            Send test email
          </Button>
        </div>
      )}
      {error != null && <ErrorState variant="inline" error={toUiError(error)} />}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove the email server?"
        description="Email channels stop delivering and the password is forgotten."
        confirmLabel="Remove email server"
        onConfirm={async () => {
          await removeSmtp();
          onSaved();
        }}
      />
    </div>
  );
}

export function AddEmailChannel({ members, disabled, onAdded }: { members: NotifyMember[]; disabled: boolean; onAdded: () => void }) {
  const [address, setAddress] = useState('');
  const [memberId, setMemberId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const who = members.find((m) => m.id === memberId);
      await addEmailChannel({ name: who ? `${who.name} email` : address.trim(), address: address.trim(), memberId: memberId || null });
      toast.success('Email address added', 'It gets budget alerts, offline machines, unmetered sessions, escalations and the daily summary.');
      setAddress('');
      onAdded();
    } catch (err) {
      const u = toUiError(err);
      setError(`${u.why} ${u.next}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <Input label="Email address" type="email" required placeholder="ops@example.com" value={address} onChange={(e) => setAddress(e.target.value)} wrapperClassName="w-72" error={error ?? undefined} />
      <Select
        label="Whose"
        value={memberId}
        options={[{ value: '', label: 'Shared, a team list' }, ...members.map((m) => ({ value: m.id, label: m.name }))]}
        onValueChange={setMemberId}
        wrapperClassName="w-56"
      />
      <Button type="submit" variant="secondary" icon={disabled ? <Mail /> : <Plus />} loading={busy} disabled={disabled || !address.trim()}>
        Add email address
      </Button>
    </form>
  );
}
